import { type TFetchError } from '@/api/api-error.types';
import { WalletUnlockRequiredError } from '@/features/wallet-access/wallet-operation';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { readPendingSend } from './pending-send';

export enum SendStatus {
  IDLE = 'idle',
  PREPARING = 'preparing',
  AWAITING_SIGNATURE = 'awaiting-signature',
  SUBMITTING = 'submitting',
  SUCCESS = 'success',
  ERROR = 'error',
  UNCERTAIN = 'uncertain',
}

export interface SendState {
  status: SendStatus;
  error: string | null;
  digest: string | null;
  requiresReauthentication: boolean;
}

export const IDLE_SEND_STATE: SendState = {
  status: SendStatus.IDLE,
  error: null,
  digest: null,
  requiresReauthentication: false,
};

export const UNCERTAIN_SEND_STATE: SendState = {
  ...IDLE_SEND_STATE,
  status: SendStatus.UNCERTAIN,
  error: 'Your transfer may have been submitted. Check its status before sending again.',
};

const UNREADABLE_PENDING_STATE: SendState = {
  ...IDLE_SEND_STATE,
  status: SendStatus.ERROR,
  error: 'Could not read pending transfers. Restore browser storage before sending.',
};

export const progressSendState = (status: SendStatus): SendState => ({
  ...IDLE_SEND_STATE,
  status,
});

export const successfulSendState = (digest: string): SendState => ({
  ...IDLE_SEND_STATE,
  status: SendStatus.SUCCESS,
  digest,
});

export const failedSendState = (error: unknown): SendState => ({
  status: SendStatus.ERROR,
  error: describeSendError(error),
  digest: null,
  requiresReauthentication: error instanceof WalletUnlockRequiredError,
});

export const restoredSendState = (address: string | undefined): SendState => {
  try {
    return address && readPendingSend(address) ? UNCERTAIN_SEND_STATE : IDLE_SEND_STATE;
  } catch {
    return UNREADABLE_PENDING_STATE;
  }
};

const describeSendError = (error: unknown): string => {
  const apiMessage = getApiErrorMessage(error as TFetchError, '');

  if (apiMessage) {
    return apiMessage;
  }

  return error instanceof Error ? error.message : 'Send failed. Please try again.';
};
